import { useState } from "react";
import { api } from "@/api/client";
import { READ_ONLY_TITLE, useReadOnly, useStore } from "@/state/store";
import { LogoutButton } from "@/components/LogoutButton";
import { IconChevronLeft, IconChevronRight } from "./SvoIcons";
import type { Phase } from "@offsite/shared";

interface Props {
  index: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  onJump: (index: number) => void;
}

const PHASE_ACTIONS: { label: string; phase: Phase; durationSec?: number }[] = [
  { label: "Themen eingeben", phase: "topics" },
  { label: "Gruppenphase starten", phase: "grouping" },
  { label: "Arbeitsphase (15 min)", phase: "working", durationSec: 15 * 60 },
  { label: "Einreichung schließen", phase: "submitted" },
  { label: "Bewertung auslösen", phase: "evaluating" },
  { label: "Bewertung zeigen", phase: "evaluated" },
  { label: "Overnight starten", phase: "overnight" },
  { label: "Reveal", phase: "reveal" },
];

export function PresenterControls({ index, total, onPrev, onNext, onJump }: Props) {
  const { state } = useStore();
  const readOnly = useReadOnly();
  const [busy, setBusy] = useState<string | null>(null);
  const [jumpTo, setJumpTo] = useState(String(index + 1));

  const runPhase = async (phase: Phase, durationSec?: number) => {
    setBusy(phase);
    try {
      if (phase === "evaluating") await api.evaluations.trigger(state.auth.token);
      else if (phase === "overnight") await api.overnight.trigger(state.auth.token);
      else await api.phase.set(state.auth.token, phase, durationSec);
    } finally {
      setBusy(null);
    }
  };

  return (
    <aside className="presenter-controls">
      <div className="pc-pager">
        <button type="button" className="btn btn-ghost-on-dark" onClick={onPrev}>
          <IconChevronLeft size={16} />
        </button>
        <span className="pc-count">
          {index + 1} / {total}
        </span>
        <button type="button" className="btn btn-ghost-on-dark" onClick={onNext}>
          <IconChevronRight size={16} />
        </button>
      </div>

      <form
        className="pc-jump"
        onSubmit={(e) => {
          e.preventDefault();
          const n = Number(jumpTo);
          if (Number.isFinite(n) && n >= 1 && n <= total) onJump(n - 1);
        }}
      >
        <input
          className="input-text"
          value={jumpTo}
          onChange={(e) => setJumpTo(e.target.value)}
          inputMode="numeric"
          aria-label="Folie springen"
          placeholder="Folie"
        />
        <button type="submit" className="btn btn-on-dark">
          Springen
        </button>
      </form>

      <div className="pc-section">
        <div className="pc-section-title">Workshop-Phase</div>
        <div className="pc-active">Aktiv: {state.workshop.phase.phase}</div>
      </div>

      <div className="pc-section">
        {PHASE_ACTIONS.map((a) => {
          const isActive = state.workshop.phase.phase === a.phase;
          return (
            <button
              key={a.phase}
              type="button"
              className={`pc-action${isActive ? " is-active" : ""}${readOnly ? " is-readonly" : ""}`}
              onClick={() => runPhase(a.phase, a.durationSec)}
              disabled={busy !== null || readOnly}
              title={readOnly ? READ_ONLY_TITLE : undefined}
            >
              {a.label}
            </button>
          );
        })}
      </div>

      <div className="pc-foot">
        <small>C schließt dieses Panel.</small>
        <LogoutButton variant="dark" />
      </div>
    </aside>
  );
}
